import Followup, {FollowupFilters, ApiFollowup} from '@/model/Followup';
import {CombinedApiResponse, doApiRequest, Method, Module} from '..';
import {parseGenericFilters} from '../meta/filters';
import {ApiCombination, ApiComparison, ApiNestedFunction, ApiSortsDerived} from '../meta/odata';
import {ResourceKontakt} from './contactHistory';
import {useSettings} from '@/store/settings';
import {useFollowupCountStore} from '@/store/followupCount';
import {ResourceKunde} from './customer';
import pinia from '@/plugins/pinia';
import moment from 'moment/moment';

export enum BedienerAction {
  Gelesen = '/{kundennr}/kontakt/{kontaktnr}/bediener/{bediener}/action/gelesen',
  Erledigt = '/{kundennr}/kontakt/{kontaktnr}/bediener/{bediener}/action/erledigt',
  BedienerEdit = '/{kundennr}/kontakt/{kontaktnr}/bediener/{bediener}',
  Bediener = '/{kundennr}/kontakt/{kontaktnr}/bediener',
}

export async function getFollowups(
    filters: FollowupFilters,
    sorts: ApiSortsDerived<ApiFollowup>,
    limit: number | null = null,
    skip: number = 0,
): Promise<CombinedApiResponse<Followup[]>> {
  const response = await doApiRequest<ApiFollowup[]>({
    method: Method.Get,
    resource: ResourceKontakt.Kontakt,
    module: Module.Kunde,
    filters: parseGenericFilters({
      ...filters,
      Bediener$Any: {
        ...filters.Bediener$Any,
        'Bediener': filters.Bediener$Any.Bediener ?? useSettings(pinia).userShort ?? '',
      },
    }),
    select: new Set([
      'KundenNr',
      'Nr',
      'KundeName',
      'KundStatus',
      'Art',
      'WiedervorlageDatum',
      'WiedervorlageUhrzeit',
      'Erledigt',
      'Thema',
    ]),
    expand: {
      Ansprechpartner: {
        select: new Set(['Nachname', 'Vorname']),
      },
      Bediener: {
        expand: {
          Gelesen: null,
          Erledigt: null,
        },
      },
    },
    sorts,
    limit,
    skip,
  });

  return [response.Data.map((p) => Followup.fromApi(p)), response.CountAll];
}

export async function getFollowupCount() {
  const response = await doApiRequest<never[]>({
    method: Method.Get,
    resource: ResourceKontakt.Kontakt,
    module: Module.Kunde,
    filters: [
      ApiCombination.And,
      [
        'Bediener',
        ApiNestedFunction.Any,
        'x',
        [
          ApiCombination.And,
          [ApiComparison.Equals, {field: 'x/Bediener'}, useSettings(pinia).userShort],
          [ApiComparison.Equals, {field: 'x/Gelesen/Status'}, false],
          [ApiComparison.Equals, {field: 'x/Erledigt/Status'}, false],
          [ApiComparison.LessThanEqual, {field: 'x/WiedervorlageDatum'}, moment().format('YYYY-MM-DD')],
        ],
      ],
    ],
    limit: 0,
    one_shot: true,
  });

  return response.CountAll;
}


export async function postBediener(params: {
  kundennr: number,
  kontaktnr: number,
  data: {
    Bediener: string,
    WiedervorlageDatum: string,
    WiedervorlageUhrzeit: string
  }
}) {
  const {kundennr, kontaktnr, data} = params;
  await doApiRequest({
    method: Method.Post,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    action: BedienerAction.Bediener,
    actionParameters: {
      kundennr,
      kontaktnr,
    },
    data,
    limit: 0,
  });

  const followupCountStore = useFollowupCountStore(pinia);
  followupCountStore.fetchFollowupCount().catch(console.error);
}

export async function putBediener(params: {
  kundennr: number,
  kontaktnr: number,
  bediener: string,
  data: {
    WiedervorlageDatum: string,
    WiedervorlageUhrzeit: string
  }
}) {
  const {kundennr, kontaktnr, bediener, data} = params;
  await doApiRequest({
    method: Method.Put,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    action: BedienerAction.BedienerEdit,
    actionParameters: {
      kundennr,
      kontaktnr,
      bediener,
    },
    data,
    limit: 0,
  });

  const followupCountStore = useFollowupCountStore(pinia);
  followupCountStore.fetchFollowupCount().catch(console.error);
}


export async function postReadFlag(params: {
  kundennr: number,
  kontaktnr: number,
  bediener: string,
  status: boolean
}) {
  const {kundennr, kontaktnr, bediener, status} = params;
  console.log('SET READ ' + bediener + ' ' + status);
  const currentUser = useSettings(pinia).userShort;
  await doApiRequest({
    method: Method.Post,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    action: BedienerAction.Gelesen,
    actionParameters: {
      kundennr,
      kontaktnr,
      bediener,
    },
    data: {
      'Status': status,
      'GesetztDurchBediener': currentUser,
    },
    limit: 0,
  });

  const followupCountStore = useFollowupCountStore(pinia);
  followupCountStore.fetchFollowupCount().catch(console.error);
}

export async function postCompleteFlag(params: {
  kundennr: number,
  kontaktnr: number,
  bediener: string,
  status: boolean
}) {
  const {kundennr, kontaktnr, bediener, status} = params;
  const currentUser = await useSettings(pinia).userShort;
  await doApiRequest({
    method: Method.Post,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    action: BedienerAction.Erledigt,
    actionParameters: {
      kundennr,
      kontaktnr,
      bediener: bediener,
    },
    data: {
      'Status': status,
      'GesetztDurchBediener': currentUser,
    },
    limit: 0,
  });

  const followupCountStore = useFollowupCountStore(pinia);
  followupCountStore.fetchFollowupCount().catch(console.error);
}

/*
export async function postBedienerWiedervorlage(params: {
  kundennr: number,
  kontaktnr: number,
  bediener: string,
  data: {
    WiedervorlageDatum: string,
    WiedervorlageUhrzeit: string
  }
}) {
  const {kundennr, kontaktnr, bediener, data} = params;
  await doApiRequest({
    method: Method.Post,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    action: BedienerAction.BedienerEdit,
    actionParameters: {
      kundennr,
      kontaktnr,
      bediener,
    },
    data,
    limit: 0,
  });

  const followupCountStore = useFollowupCountStore(pinia);
  followupCountStore.fetchFollowupCount().catch(console.error);
}
*/
export async function postContactBediener({kundennr, kontaktnr, bediener}: {
  kundennr: number,
  kontaktnr: number,
  bediener: {
    Bediener: string;
    Gelesen: boolean;
    Erledigt: boolean;
    WiedervorlageDatum: string;
    WiedervorlageUhrzeit: string;
  }
}) {
  await Promise.allSettled([
    postBediener({
      kundennr,
      kontaktnr,
      data: {
        Bediener: bediener.Bediener,
        WiedervorlageDatum: bediener.WiedervorlageDatum,
        WiedervorlageUhrzeit: bediener.WiedervorlageUhrzeit,
      },
    }),
    postCompleteFlag({
      kundennr,
      kontaktnr,
      bediener: bediener.Bediener,
      status: bediener.Erledigt,
    }),
    postReadFlag({
      kundennr,
      kontaktnr,
      bediener: bediener.Bediener,
      status: bediener.Gelesen,
    }),
  ]);
}

export async function putContactBediener({kundennr, kontaktnr, bediener}: {
  kundennr: number,
  kontaktnr: number,
  bediener: {
    Bediener: string;
    Gelesen: boolean;
    Erledigt: boolean;
    WiedervorlageDatum: string;
    WiedervorlageUhrzeit: string;
  }
}) {
  await putBediener({
    kundennr,
    kontaktnr,
    bediener: bediener.Bediener,
    data: {
      WiedervorlageDatum: bediener.WiedervorlageDatum,
      WiedervorlageUhrzeit: bediener.WiedervorlageUhrzeit,
    },
  }),
  await Promise.allSettled([
    postCompleteFlag({
      kundennr,
      kontaktnr,
      bediener: bediener.Bediener,
      status: bediener.Erledigt,
    }),
    postReadFlag({
      kundennr,
      kontaktnr,
      bediener: bediener.Bediener,
      status: bediener.Gelesen,
    }),
  ]);
}


export async function deleteContactBediener({kundennr, kontaktnr, bediener}: {
  kundennr: number,
  kontaktnr: number,
  bediener: string
}) {
  await doApiRequest({
    method: Method.Delete,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    action: BedienerAction.BedienerEdit,
    actionParameters: {
      kundennr,
      kontaktnr,
      bediener,
    },
    limit: 0,
  });
}
